import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Lock, Eye, EyeOff, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { supabase } from '../lib/supabase';
import useCalendarStore from '../store/useCalendarStore';

export default function ResetPasswordPage() {
  const darkMode = useCalendarStore(s => s.darkMode);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Check if we have a session (Supabase Auth sets this automatically from the hash fragment)
    const checkSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        // If no session, it might be an invalid or expired link
        // However, Supabase sometimes takes a moment to process the fragment
        // We'll give it a chance, but if it's really missing, we'll alert the user
      }
    };
    checkSession();
  }, []);

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) throw error;
      
      setSuccess(true);
      toast.success('Password updated successfully!');
      
      // Auto-redirect after 3 seconds
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err) {
      toast.error(err.message || 'Failed to update password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center login-bg">
      <div className="w-full max-w-md px-4">
        {/* Header */}
        <div className="flex flex-col items-center justify-center mb-12">
          <div className="flex items-center gap-5 mb-4">
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-tr from-blue-600/20 to-cyan-400/20 blur-2xl rounded-full opacity-60" />
              <img src={darkMode ? "/logo.png" : "/logo_black.png"} alt="ScholarsAnchor Logo" className="w-20 h-20 object-contain relative z-10" />
            </div>
            <div className="flex flex-col relative z-10 leading-[0.8]">
              <h1 className="text-5xl font-black text-zinc-900 dark:text-white tracking-tighter uppercase">
                Scholars
              </h1>
              <span className="text-xl font-black tracking-[0.32em] text-blue-600 dark:text-blue-500 uppercase">
                Anchor
              </span>
            </div>
          </div>
          <div className="flex flex-col items-center">
            <div className="h-px w-16 bg-gradient-to-r from-transparent via-zinc-300 dark:via-zinc-700 to-transparent mb-4" />
            <p className="text-[0.7rem] sm:text-xs text-zinc-500 dark:text-zinc-400 uppercase tracking-[0.4em] font-black text-center max-w-[280px] sm:max-w-none leading-relaxed">
              Intelligent Academic Portal
            </p>
          </div>
        </div>
        <div className="bg-white dark:bg-zinc-900 rounded-2xl shadow-xl border border-zinc-200 dark:border-zinc-800 p-8">
          <div className="flex items-center gap-2 mb-6">
            <Lock className="w-5 h-5 text-blue-600" />
            <h2 className="text-lg font-semibold text-zinc-800 dark:text-zinc-100">Reset Password</h2>
          </div>

          {!success ? (
            <>
              <p className="text-xs text-zinc-500 mb-6">
                Please enter your new password below.
              </p>
              <form onSubmit={handleResetPassword} className="space-y-4">
                <div>
                  <Label className="text-xs font-medium text-zinc-600 dark:text-zinc-400">New Password</Label>
                  <div className="relative mt-1.5">
                    <Input
                      type={showPw ? 'text' : 'password'}
                      placeholder="Minimum 6 characters"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pr-10"
                      required
                    />
                    <button 
                      type="button" 
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600 p-1.5 flex items-center justify-center focus:outline-none" 
                      onClick={() => setShowPw(!showPw)}
                    >
                      {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div>
                  <Label className="text-xs font-medium text-zinc-600 dark:text-zinc-400">Confirm New Password</Label>
                  <Input
                    className="mt-1.5"
                    type="password"
                    placeholder="Repeat your password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white h-11 text-sm font-medium"
                  disabled={loading}
                >
                  {loading ? 'Updating...' : 'Reset Password'}
                </Button>
              </form>
            </>
          ) : (
            <div className="text-center py-4">
              <div className="flex justify-center mb-4">
                <CheckCircle2 className="w-12 h-12 text-green-500" />
              </div>
              <h3 className="text-lg font-bold text-zinc-800 dark:text-zinc-100 mb-2">Success!</h3>
              <p className="text-sm text-zinc-500 mb-6">
                Your password has been updated. You will be redirected to the login page shortly.
              </p>
              <Link to="/login">
                <Button className="w-full bg-zinc-800 hover:bg-zinc-900 text-white">
                  Go to Sign In Now
                </Button>
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
